/**
 * THE SURFACE BAND — how a solid becomes a SMOOTH surface, and the one place the rule lives.
 *
 * A voxel's weight is a signed distance, and SurfaceNets puts its vertex at `t = w0 / (w0 - w1)`
 * along each crossing edge. That fraction is the ONLY thing that makes a voxel surface smooth. Write
 * `+0.5` for every solid voxel and `-0.5` for every air one and every crossing computes `t = 0.5`:
 * the vertex lands dead centre in its cell, all sub-voxel information is gone, and the mesh is the
 * raw voxelisation of the shape. Diagonal and curved walls step; the mesher rounds the steps off and
 * you get melted Lego. It is worse than the silhouette suggests, because normals are accumulated
 * from triangle normals — a plane whose vertices scatter by half a voxel does not shade like a plane.
 *
 * Measured over one chunk, weight histograms air → solid: a city tower read `24934 / 0 / 7834` —
 * not one voxel in between. Real terrain over the same span read `408 / 470 spread across all
 * fourteen intermediate levels / 31892`. That gap is the whole defect.
 *
 * SO THE RULE IS: NO SURFACE IN THIS WORLD IS BINARY. Every feature that draws a solid measures how
 * far each voxel is from its own surface and writes that, both sides of it.
 *
 * ---
 *
 * THE SHAPE THAT COVERS ALMOST EVERYTHING IS AN EXTRUSION. Buildings, plinths, floor slabs,
 * partitions, boundary walls, the coarse city mass, even a stair tread: a 2D region, extruded
 * between two heights. So the query belongs PER COLUMN — one horizontal distance and a Y range —
 * and the Y loop is then pure arithmetic. That matters: a per-voxel distance callback in the
 * generator's inner loop is a megamorphic call in the hottest code in the project, which is exactly
 * what the perf guide's invariants exist to prevent.
 *
 * THE BAND IS ONE COARSE VOXEL, and that is forced rather than chosen: `coarseWeight` saturates at a
 * distance of one lattice voxel, so a voxel further out than `strideFine` cannot carry a partial
 * weight and writing it changes nothing. `surfaceBandFine` is that number, and it is the SAME
 * constant that every AABB, ySpan and clip must grow by — a feature that draws a band one voxel
 * further out than its declared bounds is a hole at one chunk plane, at one LOD, for one seed.
 *
 * MERGING IS A MAX, and that is the no-carve rule stated numerically rather than as a special case.
 * A max can never lower a voxel, so it can never erode terrain, never cut a hole, and never depend
 * on the order features are drawn in. The negative half of a band is a max too: `-0.25` beats the
 * `-0.5` of open air and loses to anything solid, which is precisely the behaviour wanted.
 *
 * MATERIAL MOVES ONLY WHEN THE RESULT IS SOLID, or a band would recolour the air around every wall.
 * The threshold is `weight >= 0`, NOT `> 0`: `packVoxel(0)` normalises to 0.5, rounds to packed 8,
 * and decodes to +0.033 — solid to the mesher and to the physics field alike (voxelData's
 * SURFACE_PACKED_THRESHOLD is 7). A voxel sitting exactly on the surface is the topmost row of every
 * feature in the world, so getting this bound wrong leaves every roof and wall-top the colour of
 * whatever was underneath it.
 *
 * ADDING A BAND DOES NOT ADD TRIANGLES. A voxel is solid iff its centre is inside, and that test is
 * the same before and after — so the sign pattern is unchanged, the crossing cells are the same
 * cells, and the vertex and triangle counts are identical. Only the positions move.
 *
 * ...WITH ONE COUNTING CAVEAT, which is worth knowing before reading a solid-voxel diff. A sample
 * lying EXACTLY on a face has depth 0 and packs solid, so a slab whose width equals the sample
 * spacing owns both of its boundary samples where the old boolean owned one. Measured on a 0.5 m
 * building wall at lod 1: 16% more "solid" voxels, and no change at all to the drawn or collidable
 * wall, whose two crossings still land on its two faces. Count crossings, not solids.
 */
import { coarseWeight, getWeight, setMaterial, setWeight } from './voxelData.js';
/**
 * How far outside a solid its band reaches, in fine voxels, on a lattice of `strideFine`.
 *
 * One coarse voxel. Everything that declares bounds — a node's `aabb`, its `ySpan`, the coarse
 * column clip — must grow by this, and must grow by THIS rather than by its own `+1`, or the three
 * drift apart and the seam only shows for one seed at one level.
 */
export function surfaceBandFine(strideFine) {
    return strideFine;
}
/**
 * Depth inside an extruded footprint, in fine voxels — positive inside, negative outside.
 *
 * `d2Fine` is the signed horizontal distance to the footprint's edge (negative inside). Pass
 * `-Infinity` for `yBottomFine` when the solid has no free bottom surface — a wall standing on the
 * ground has none, and measuring one would fade the wall out at its own base.
 */
export function extrudedDepth(d2Fine, yBottomFine, yTopFine, yFine) {
    return -Math.max(d2Fine, yBottomFine - yFine, yFine - yTopFine);
}
/** `extrudedDepth` as a weight on this level's lattice. The value to max-merge. */
export function extrudedWeight(d2Fine, yBottomFine, yTopFine, yFine, strideFine) {
    return coarseWeight(extrudedDepth(d2Fine, yBottomFine, yTopFine, yFine), strideFine);
}
/**
 * Weight at or above which a merged voxel packs SOLID, and therefore at or above which the feature's
 * material wins. See the header: `packVoxel(0)` is solid, so this bound is inclusive.
 */
export const SOLID_WEIGHT_FLOOR = 0;
/**
 * THE THINNEST SURFACE THIS LATTICE CAN HOLD, in lattice voxels — the L1 norm of its own normal.
 *
 * Not a heuristic. A digital plane with normal (a, b, c) is 6-connected — tunnel-free — only where
 * its thickness reaches |a| + |b| + |c|; below that it winks in and out as its offset from the
 * lattice drifts, because a sample lying exactly on a face has depth 0 and packs SOLID while one a
 * hair off-centre packs to 7 and is air. Measured on the trilinear iso-surface the mesher draws and
 * the physics field collides against, before this existed:
 *
 *   surface                 lattice thickness   mean     thinnest 5%   holes
 *   exterior wall, lod 0      2.0 voxels        0.524 m    0.480 m      0 / 832
 *   exterior wall, lod 1      1.0               0.395      0.270        3
 *   exterior wall, lod 2      1.0               0.857      0.490       12
 *   room partition, lod 0     1.0               0.268      0.130       19 / 11346
 *
 * The only one at two voxels is solid; everything at one pulses and drops out. Flooring at the L1
 * bound took every hole to zero and doubled the thinnest 5%.
 *
 * EXACT WHERE THE ORIENTATION IS KNOWN, SAFE CONSTANT WHERE IT IS NOT. A city building knows its own
 * yaw, so it passes `|cos| + |sin|` — which is `CityGrid.reach`, the same quantity the road
 * downsample already needed, for the same reason. A build part does not know how it will be turned,
 * so it takes REACH_ANY. A floor slab's normal is fixed on an axis and takes REACH_AXIS, which is why
 * this is a parameter and not one blanket sqrt(3): that would make every floor and roof 73% thicker
 * for nothing.
 *
 * IT IS A FLOOR UNDER AN AUTHORED SIZE, NEVER THE SIZE ITSELF. This is the mistake it invites, and
 * it is quiet: give a surface a dimension BELOW the bound and `max(authored, floor)` returns the
 * floor, so the floor silently becomes the whole specification. What you get is legal — the city's
 * balcony parapet was authored at 0.25 m, floored to 0.354, and measured ZERO holes at every level,
 * exactly as promised — and it looks wrong anyway, because sitting ON the bound is the thinnest a
 * thing is permitted to be: sqrt(2) samples, reconstructing at 84% of that (0.297 m measured), with
 * barely a sample between its two crossings for the mesher to interpolate. This buys CONNECTIVITY,
 * not appearance. Author above it — every other wall in that building is 0.5 m and only meets the
 * floor at coarse levels, which is what the floor is for — and let it bite where it is meant to.
 *
 * AND IT IS NOT A CLAMP APPLIED EVERYWHERE. Some features are sub-voxel ON PURPOSE — `pinPart` is a
 * one-voxel column because a trunk wide enough to read as a body at lod 3 would be a pillar, and
 * coarseModel's second rule DROPS a step it cannot draw rather than fattening it ("clamping several
 * tip steps to the same minimum just makes a fatter spike"). Thicken at the authoring boundary where
 * the shape is a wall; refuse, at the coarse levels, where it is a detail. Never silently in the
 * shape evaluator, which every stand-in and every preset shares.
 */
export const REACH_AXIS = 1;
export const REACH_VERTICAL = Math.SQRT2;
export const REACH_ANY = Math.sqrt(3);
export function minThicknessFine(reach, strideFine) {
    return reach * strideFine;
}
/**
 * The smallest HALF-extent a build part may have, in fine voxels — REACH_ANY, because a part is
 * turned freely and cannot know at authoring time which way its faces will end up pointing.
 *
 * The build UI had already found this by feel: `SIZE_VALUES` started at 0.71 and the wall, panel and
 * ramp presets all say `size(4, 4, 0.71)`. That is REACH_VERTICAL / 2 rounded — right for a part that
 * stays upright, 18% short for a ramp, which is tilted. Same number, now with a name and the other
 * 18%.
 *
 * TORCH_PARTS KEEPS THE LITERAL 0.71 and should. A torch is upright by construction, so
 * REACH_VERTICAL is its true bound and this one would fatten it 22% for a rotation it never takes —
 * and it is the one shape terrain and the build system share, so swapping the last two digits for the
 * exact sqrt(2)/2 re-baselines stampCache AND the trajectory checksum to move a decorative column by
 * 0.4%. A guard is worth more standing still than that is worth tidying.
 */
export const MIN_PART_HALF_EXTENT = minThicknessFine(REACH_ANY, 1) * 0.5;
/**
 * The smallest RADIUS a TUBE may be drawn at — a branch, a pipe, a rail, anything round along an
 * arbitrary axis — in fine voxels, on a lattice of `strideFine`: half the wall bound for a freely
 * turned surface, √3/2 ≈ 0.87 lattice voxels.
 *
 * WHY IT IS ITS OWN RULE. `minThicknessFine` is stated as a THICKNESS across a wall; a tube's authored
 * number is a RADIUS, and the first model to draw tubes derived its floor from the wall bound by hand.
 * That is the drift this exists to stop: the conversion lives here, once, and `blobs.taperedSegment`
 * clamps to it, so no model re-derives it.
 *
 * WHAT THE BOUND BUYS is connectivity, as its parent does: at radius √3/2 every point of the axis has
 * a lattice sample inside the tube (√3/2 is the furthest any point can be from the lattice), so the
 * solid set is one connected body. Measured on the big oak's twigs at exactly this radius: 4931 bark
 * samples, ONE 6-connected component. What it does not buy is a solid core — the sample nearest an
 * axis point may be at depth ≈ 0 — so twigs at the floor are the thinnest things the mesher draws,
 * and the lod-1 FOLD breaks them into pieces (15 components, 88% in the main body). A floor of 1.5
 * voxels was tried against that: cores at 40% of bark, the fold 98% in one body — and on screen a
 * 0.75 m twig read as far too thick for a tree. The lattice's minimum is the tree's twig; author
 * the layers above it (treeOakLarge's LAYER_R) and accept the fold's dotting at range.
 */
export function minTubeRadiusFine(strideFine) {
    return minThicknessFine(REACH_ANY, strideFine) * 0.5;
}
/**
 * The half-width a feature must be drawn at to survive this lattice, in the same units as `half`.
 *
 * A max-downsample makes a sub-voxel feature REGISTER on a coarse column; it does not make it thick.
 * Measure the true half-width of a 1 m wall against an 8 m lod-5 voxel and the band never saturates:
 * the wall peaks around weight 0.2 and reads as a translucent sheet — the dissolve that fold.ts's
 * "strongest wins the cell" exists to prevent, arriving through the back door. So the half-width is
 * FLOORED AT HALF THE MINIMUM THICKNESS ABOVE — the distance-domain form of the "floored at one
 * coarse voxel" rule the wall's height, the canopy dot, the road and the block silhouette all
 * already obey, with the one correction that the constant is the normal's L1 norm and not 1.
 */
export function flooredHalfWidth(half, strideFine, voxelScale, reach) {
    return Math.max(half, minThicknessFine(reach, strideFine) * voxelScale * 0.5);
}
/**
 * Depth inside an axis-aligned box, from its three per-axis OVERHANGS — `|d| - half` on each axis,
 * which is what a caller precomputes per column and reuses down the whole Y loop.
 *
 * The exact box distance, not the Chebyshev one. `min(hu-|du|, hv-|dv|, ...)` is right inside and
 * wrong outside every corner, saying "closer" than the truth — and a corner is exactly where a
 * rotated building's band shows. Two boxes' union is the MAX of their depths; A minus B is
 * `min(depthA, -depthB)`.
 */
export function boxDepth3(qu, qv, qy) {
    const inside = qu > qv ? (qu > qy ? qu : qy) : (qv > qy ? qv : qy);
    if (inside <= 0)
        return -inside;
    const ou = qu > 0 ? qu : 0, ov = qv > 0 ? qv : 0, oy = qy > 0 ? qy : 0;
    return -Math.sqrt(ou * ou + ov * ov + oy * oy);
}
/** `boxDepth3` in the plane — for a footprint whose Y range is handled separately. */
export function boxDepth2(qu, qv) {
    const inside = qu > qv ? qu : qv;
    if (inside <= 0)
        return -inside;
    const ou = qu > 0 ? qu : 0, ov = qv > 0 ? qv : 0;
    return -Math.sqrt(ou * ou + ov * ov);
}
/** One axis's overhang: how far `x` lies outside `[centre - half, centre + half]`. */
export function slabQ(x, centre, half) {
    const d = x - centre;
    return (d < 0 ? -d : d) - half;
}
/** ...and the same for an interval given by its ends. */
export function spanQ(x, lo, hi) {
    return slabQ(x, (lo + hi) * 0.5, (hi - lo) * 0.5);
}
/**
 * MAX-MERGE one surface sample into a packed voxel word — the only way content should reach a chunk.
 *
 * A max cannot lower a voxel, so it cannot erode terrain, cut a hole, or depend on the order features
 * are drawn in: that is the no-carve rule stated numerically rather than as a per-site special case
 * (`if (getWeight(...) >= 0.5) continue`, written out at three sites before this existed).
 *
 * Material follows only where the result is SOLID, or the negative half of a band would recolour the
 * air around every wall. `weight >= 0` and not `> 0`, because packVoxel(0) packs to 8 and decodes
 * positive — see the header. Light is preserved, so a merge into a relit chunk keeps its lighting.
 */
export function mergeSurfaceWord(packed, weight, material) {
    if (weight <= getWeight(packed))
        return packed;
    const w = setWeight(packed, weight);
    return weight >= SOLID_WEIGHT_FLOOR ? setMaterial(w, material) : w;
}
//# sourceMappingURL=surfaceBand.js.map